import React, { useState, useMemo, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  Edit,
  Trash2,
  Target,
  BarChart3,
  Calendar,
  Database,
  FileText,
  Plus,
  BrainCircuit,
  Loader2,
  CheckCircle,
  XCircle
} from 'lucide-react';
import VraisemblanceMatrixModal from './VraisemblanceMatrixModal';
import ScenarioRiskCalculation from './ScenarioRiskCalculation';
import ModesOperatoiresDisplay from './ModesOperatoiresDisplay';
import ModesOperatoiresFlowVisualization from './ModesOperatoiresFlowVisualization';
import ModeComparisonPanel from './ModeComparisonPanel';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';
import { useAuth } from '../../context/AuthContext';

import { api } from '../../api/apiClient';


/**
 * ScenarioManager - Component for displaying a scenario per attack path
 */
const ScenarioManager = ({
  scenarios = [],
  existingScenarios = [],
  scenarioStats = null,
  selectedAttackPath = null,
  onEditScenario,
  onDeleteScenario,
  onScenarioUpdate,
  onDeleteMode,
  onToggleRetained,
  onPhaseVisualization,
  onAddMode,
  onSaveCritique,
  analysisId,
  isLoading = false,
  onVraisemblancePreview,
  onCaptureReady
}) => {

  const { t } = useTranslation();
  const { companyAiEnabled } = useAuth();
  const [showVraisemblanceMatrix, setShowVraisemblanceMatrix] = useState(false);

  // Collapsible sections state
  const [isRiskExpanded, setIsRiskExpanded] = useState(true);
  const [isSynthesisExpanded, setIsSynthesisExpanded] = useState(true);
  const [isModesExpanded, setIsModesExpanded] = useState(true);

  // AI mode generation state
  const [showModeChoice, setShowModeChoice] = useState(false);
  const [aiGeneratedResult, setAiGeneratedResult] = useState(null);
  const [isGeneratingMode, setIsGeneratingMode] = useState(false);
  const [showAIPreview, setShowAIPreview] = useState(false);
  const [generationError, setGenerationError] = useState(null);

  // Holds the AI preview graph's PNG capture function (preview already shows the new mode)
  const previewCaptureRef = useRef(null);

  // Mode list tabs: manually entered vs Assistant ACG generated
  const [modeTab, setModeTab] = useState('manual');

  // Delete mode confirmation state
  const [deleteModeState, setDeleteModeState] = useState({
    isOpen: false,
    modeId: null,
    modeName: '',
  });

  const allScenarios = [...existingScenarios, ...scenarios];

  const scenarioKey = (s) => `${s.name}::${s.attackPathId}`;

  // Selected scenario (by key); falls back to the first scenario
  const [selectedScenarioKey, setSelectedScenarioKey] = useState(null);

  const currentScenario = useMemo(() => {
    if (allScenarios.length === 0) return null;
    const selected = selectedScenarioKey
      ? allScenarios.find((s) => scenarioKey(s) === selectedScenarioKey)
      : null;
    return selected || allScenarios[0];
  }, [allScenarios, selectedScenarioKey]);

  // Reset selection when the attack path changes
  useEffect(() => {
    setSelectedScenarioKey(null);
  }, [selectedAttackPath?.id]);

  // Mode list tabs: manually entered vs Assistant ACG generated
  const isAiMode = (mode, sc = currentScenario) => String(mode.id || '').startsWith('mode_ai_') || (sc?.metadata?.generatedByAI && String(mode.id || '').startsWith('mo_')); // retroactive: legacy mo_* under AI scenario
  const allModes = currentScenario?.modesOperatoires || [];
  const aiModeCount = allModes.filter((m) => isAiMode(m, currentScenario)).length;
  const manualModeCount = allModes.length - aiModeCount;
  const visibleModes = modeTab === 'ai'
    ? allModes.filter((m) => isAiMode(m, currentScenario))
    : allModes.filter((m) => !isAiMode(m, currentScenario));
  const manualModes = allModes.filter((m) => !isAiMode(m, currentScenario));
  const aiModes = allModes.filter((m) => isAiMode(m, currentScenario));

  // ponytail: allow viewing empty manual tab — was instant redirect to ai
  useEffect(() => {
    if (modeTab === 'compare' && aiModeCount === 0) setModeTab(manualModeCount > 0 ? 'manual' : 'ai');
  }, [modeTab, manualModeCount, aiModeCount]);

  // Toggle all sections
  const toggleAllSections = () => {
    const allExpanded = isRiskExpanded && isSynthesisExpanded && isModesExpanded;
    const newState = !allExpanded;
    setIsRiskExpanded(newState);
    setIsSynthesisExpanded(newState);
    setIsModesExpanded(newState);
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'Très faible': 'text-green-600 bg-green-100',
      'Faible': 'text-yellow-600 bg-yellow-100',
      'Moyen': 'text-orange-600 bg-orange-100',
      'Élevé': 'text-red-600 bg-red-100',
      'Très élevé': 'text-red-800 bg-red-200',
      'Very Low': 'text-green-600 bg-green-100',
      'Low': 'text-yellow-600 bg-yellow-100',
      'Medium': 'text-orange-600 bg-orange-100',
      'High': 'text-red-600 bg-red-100',
      'Very High': 'text-red-800 bg-red-200'
    };
    return colors[severity] || 'text-gray-600 bg-gray-100';
  };

  const getScenarioSource = (scenario) => {
    if (scenario.metadata?.generatedByAI) {
      return {
        type: 'AI',
        icon: <BarChart3 className="h-4 w-4" />,
        label: `ACG - Tentative #${scenario.generationAttempt || 1}`,
        color: 'text-blue-600 bg-blue-100'
      };
    }
    return null;
  };

  const getVraisemblanceColor = (vraisemblance) => {
    switch (vraisemblance) {
      case 1: return 'bg-green-50 text-green-600 border border-green-200'; // Faible
      case 2: return 'bg-yellow-50 text-yellow-600 border border-yellow-200'; // Moyenne
      case 3: return 'bg-orange-50 text-orange-600 border border-orange-200'; // Élevée
      case 4: return 'bg-red-50 text-red-600 border border-red-200'; // Très élevée
      default: return 'bg-gray-50 text-gray-600 border border-gray-200';
    }
  };

  const getVraisemblanceLabel = (vraisemblance) => {
    switch (vraisemblance) {
      case 1: return t('common.likelihood.low', 'Peu vraisemblable');
      case 2: return t('common.likelihood.medium', 'Vraisemblable');
      case 3: return t('common.likelihood.high', 'Très vraisemblable');
      case 4: return t('common.likelihood.critical', 'Quasi certain');
      default: return 'N/A';
    }
  };

  const handleAIGenerateMode = async () => {
    if (!currentScenario) return;
    setGenerationError(null);
    setIsGeneratingMode(true);
    setShowModeChoice(false);
    try {
      const steps = currentScenario.operationalSteps || currentScenario.steps || [];
      const modes = currentScenario.modesOperatoires || [];
      const response = await api.post('/ai/generate-mode-operatoire', {
        analysisId: analysisId || selectedAttackPath?.analysisId || currentScenario?.analysisId,
        attackPath: selectedAttackPath || null,
        businessContext: currentScenario.businessContext || null,
        vulnerabilities: selectedAttackPath?.vulnerabilities || [],
        attackTechniques: selectedAttackPath?.attackTechniques || [],
        operationalSteps: steps,
        existingModes: modes,
      });
      if (response?.success && response?.data) {
        setAiGeneratedResult(response.data);
        setShowAIPreview(true);
      } else {
        setGenerationError('Réponse inattendue du serveur. Veuillez réessayer.');
      }
    } catch (err) {
      console.error('[ScenarioManager] AI mode generation failed:', err);
      setGenerationError("L'Assistant ACG a échoué. Vérifiez que le service AI est actif et réessayez.");
    } finally {
      setIsGeneratingMode(false);
    }
  };

  const handleAcceptAIMode = async () => {
    if (!aiGeneratedResult || !currentScenario) return;
    const newActions = aiGeneratedResult.newActions || [];
    const mode = aiGeneratedResult.mode || {};

    // Add AI-generated actions like the manual edit UI does (addAction)
    const newSteps = (newActions).map((a) => ({
      elementaryActionId: a.elementaryActionId,
      phase: a.phase,
      technique: a.name,
      name: a.name,
      description: a.description || '',
      successProbability: a.ps,
      ps: a.ps,
      technicalDifficulty: a.dt,
      dt: a.dt,
      tools: a.tools || [],
      duration: a.duration || '',
      dependencies: [],
      nextActions: [],
    }));

    const updatedSteps = [
      ...(currentScenario.operationalSteps || currentScenario.steps || []),
      ...newSteps,
    ];

    // Create mode with the AI path — IDs match directly (like addMode in edit UI)
    const newMode = {
      id: `mode_ai_${Date.now()}`,
      name: mode.name || 'Nouveau mode AI',
      description: mode.description || '',
      path: mode.path || [],
      vraisemblance: 2,
    };

    // Capture the preview graph (already shows the new mode) and save it with the scenario
    const png = previewCaptureRef.current
      ? await previewCaptureRef.current()
      : null;

    const updatedScenario = {
      ...currentScenario,
      operationalSteps: updatedSteps,
      modesOperatoires: [...(currentScenario.modesOperatoires || []), newMode],
      ...(png
        ? { vizPng: png.dataUrl, vizPngWidth: png.width, vizPngHeight: png.height }
        : {}),
    };
    onScenarioUpdate && onScenarioUpdate(updatedScenario);
    setShowAIPreview(false);
    setAiGeneratedResult(null);
    setShowModeChoice(false);
  };

  const handleDeleteMode = (modeId) => {
    if (!currentScenario) return;
    const mode = (currentScenario.modesOperatoires || []).find(m => m.id === modeId);
    setDeleteModeState({
      isOpen: true,
      modeId,
      modeName: mode?.name || 'Mode sans nom',
    });
  };

  const handleConfirmDeleteMode = () => {
    if (!currentScenario || !deleteModeState.modeId) return;
    if (onDeleteMode) {
      onDeleteMode(currentScenario.id || currentScenario._id, deleteModeState.modeId);
    } else {
      const updatedScenario = {
        ...currentScenario,
        modesOperatoires: (currentScenario.modesOperatoires || []).filter(
          (m) => m.id !== deleteModeState.modeId,
        ),
      };
      onScenarioUpdate && onScenarioUpdate(updatedScenario);
    }
    setDeleteModeState({ isOpen: false, modeId: null, modeName: '' });
  };

  // Toggle the "retenu" flag on a mode of the current scenario and propagate up
  const handleToggleRetained = (modeId) => {
    if (!currentScenario) return;
    const updatedModes = (currentScenario.modesOperatoires || []).map((m) =>
      m.id === modeId ? { ...m, retained: !(m.retained !== false) } : m
    );
    const retainedVs = updatedModes
      .filter((m) => m.retained !== false)
      .map((m) => m.vraisemblance)
      .filter((v) => v >= 1 && v <= 4);
    const newScenarioV = retainedVs.length > 0 ? Math.max(...retainedVs) : null;
    const updatedScenario = {
      ...currentScenario,
      modesOperatoires: updatedModes,
      scenarioVraisemblance: newScenarioV,
    };
    if (onToggleRetained) {
      onToggleRetained(updatedScenario, modeId);
    } else {
      onScenarioUpdate && onScenarioUpdate(updatedScenario);
    }
  };

  const handleRefuseAIMode = () => {
    setShowAIPreview(false);
    setAiGeneratedResult(null);
  };

  const handleRetryAIMode = () => {
    setShowAIPreview(false);
    setAiGeneratedResult(null);
    handleAIGenerateMode();
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  if (allScenarios.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center py-8">
          <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('workshop4.operationalScenarios.scenarioManager.noScenarioGenerated', 'Aucun scénario généré')}
          </h3>
          <p className="text-gray-600">
            {t('workshop4.operationalScenarios.scenarioManager.generateFirstScenario', 'Générez votre premier scénario opérationnel pour ce chemin d\'attaque.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      {/* Header with navigation and stats */}
      <div className="bg-white p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center border border-blue-100">
              <Target className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-800">
                {t('workshop4.operationalScenarios.scenarioManager.title', 'Scénarios Opérationnels')}
              </h3>
              {selectedAttackPath && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                  {selectedAttackPath.referenceCode || selectedAttackPath.pathName}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={toggleAllSections}
              className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-all duration-200 shadow-sm"
              title={isRiskExpanded && isSynthesisExpanded && isModesExpanded ? t('workshop4.operationalScenarios.scenarioManager.collapseAll', 'Réduire tout') : t('workshop4.operationalScenarios.scenarioManager.expandAll', 'Développer tout')}
            >
              {isRiskExpanded && isSynthesisExpanded && isModesExpanded ? (
                <>
                  <ChevronLeft className="h-4 w-4 mr-2 rotate-90" />
                  {t('workshop4.operationalScenarios.scenarioManager.collapseAll', 'Réduire tout')}
                </>
              ) : (
                <>
                  <ChevronRight className="h-4 w-4 mr-2 -rotate-90" />
                  {t('workshop4.operationalScenarios.scenarioManager.expandAll', 'Développer tout')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Current scenario display */}
      {currentScenario && (
        <div className="p-4 bg-gradient-to-br from-gray-50 to-white">
          {/* Scenario selector */}
          {allScenarios.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {allScenarios.map((s) => {
                const key = scenarioKey(s);
                const isActive =
                  key === (selectedScenarioKey || scenarioKey(allScenarios[0]));
                return (
                  <button
                    key={key}
                    onClick={() => setSelectedScenarioKey(key)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${isActive
                      ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                      : 'bg-white text-slate-600 border-slate-300 hover:border-blue-400'
                      }`}
                  >
                    {s.name}
                  </button>
                );
              })}
            </div>
          )}

          {/* Scenario header */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3">
                  <h4 className="text-2xl font-bold text-gray-900">
                    {currentScenario.name}
                  </h4>
                  {getScenarioSource(currentScenario) && (
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm ${getScenarioSource(currentScenario).color}`}>
                      {getScenarioSource(currentScenario).icon}
                      <span className="ml-1.5">{getScenarioSource(currentScenario).label}</span>
                    </span>
                  )}
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex items-start space-x-2 ml-6">
                <button
                  onClick={() => onEditScenario && onEditScenario(currentScenario)}
                  className="inline-flex items-center px-4 py-2.5 rounded-lg text-sm font-medium bg-white text-gray-700 hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md border border-gray-300"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  {t('workshop4.operationalScenarios.scenarioManager.edit', 'Modifier')}
                </button>
                <button
                  onClick={() => onDeleteScenario && onDeleteScenario(currentScenario)}
                  className="inline-flex items-center px-4 py-2.5 rounded-lg text-sm font-medium bg-white text-red-600 hover:bg-red-50 transition-all duration-200 shadow-sm hover:shadow-md border border-red-300"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('workshop4.operationalScenarios.scenarioManager.delete', 'Supprimer')}
                </button>
              </div>
            </div>
          </div>




          {/* Scenario Risk Calculation - Collapsible */}
          <div className="mb-4">
            <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
              <button
                onClick={() => setIsRiskExpanded(!isRiskExpanded)}
                className="w-full bg-gradient-to-r from-blue-50 via-sky-50 to-blue-50 px-4 py-3 hover:from-blue-100 hover:via-sky-100 hover:to-blue-100 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-sky-600 rounded-lg flex items-center justify-center shadow-sm">
                      <BarChart3 className="h-5 w-5 text-white" />
                    </div>
                    <div className="text-left">
                      <h5 className="text-base font-semibold text-gray-900">{t('workshop4.riskAssessment.title', 'Risk Assessment')}</h5>
                      <p className="text-xs text-gray-600 mt-0.5">{t('workshop4.riskAssessment.subtitle', 'Likelihood and metrics calculation')}</p>
                    </div>
                  </div>
                  <ChevronRight
                    className={`h-5 w-5 text-gray-600 transition-transform duration-300 ${isRiskExpanded ? 'rotate-90' : ''
                      }`}
                  />
                </div>
              </button>

              <div className={`transition-all duration-300 ease-in-out ${isRiskExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                } overflow-hidden`}>
                <div className="p-6 bg-gradient-to-br from-white to-gray-50">
                  <ScenarioRiskCalculation
                    scenario={currentScenario}
                    onScenarioUpdate={onScenarioUpdate}
                    onVraisemblancePreview={onVraisemblancePreview}
                    isEditable={true}
                    showEditButton={true}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Synthèse Professionnelle - Simple paragraph format */}
          {currentScenario.professionalSynthesis && (
            <div className="mb-4">
              <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
                <button
                  onClick={() => setIsSynthesisExpanded(!isSynthesisExpanded)}
                  className="w-full bg-gradient-to-r from-indigo-50 via-blue-50 to-indigo-50 px-5 py-4 hover:from-indigo-100 hover:via-blue-100 hover:to-indigo-100 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-sm">
                        <FileText className="h-5 w-5 text-white" />
                      </div>
                      <div className="text-left">
                        <h5 className="text-base font-semibold text-gray-900">{t('workshop4.operationalScenarios.scenarioManager.professionalSynthesis.title', 'Synthèse Professionnelle')}</h5>
                        <p className="text-xs text-gray-600 mt-0.5">{t('workshop4.operationalScenarios.scenarioManager.professionalSynthesis.subtitle', 'Analyse détaillée du scénario d\'attaque')}</p>
                      </div>
                    </div>
                    <ChevronRight
                      className={`h-5 w-5 text-gray-600 transition-transform duration-300 ${isSynthesisExpanded ? 'rotate-90' : ''
                        }`}
                    />
                  </div>
                </button>

                <div className={`transition-all duration-300 ease-in-out ${isSynthesisExpanded ? 'max-h-[4000px] opacity-100' : 'max-h-0 opacity-0'
                  } overflow-hidden`}>
                  <div className="p-6 bg-gradient-to-br from-white to-gray-50">
                    <div className="bg-gradient-to-r from-slate-50 to-blue-50 p-5 rounded-xl border border-slate-200 shadow-sm">
                      <div className="prose prose-sm max-w-none">
                        {/* Handle string format directly */}
                        {typeof currentScenario.professionalSynthesis === 'string' ? (
                          <p className="leading-relaxed text-gray-700 text-justify">
                            {currentScenario.professionalSynthesis}
                          </p>
                        ) : (
                          /* Handle object format - combine all text into a single paragraph */
                          <p className="leading-relaxed text-gray-700 text-justify">
                            {[
                              currentScenario.professionalSynthesis.overview,
                              currentScenario.professionalSynthesis.detailedAnalysis,
                              currentScenario.professionalSynthesis.attackDescription,
                              typeof currentScenario.professionalSynthesis.phases === 'string' ? currentScenario.professionalSynthesis.phases : null,
                              currentScenario.professionalSynthesis.riskEvaluation || currentScenario.professionalSynthesis.evaluation,
                              currentScenario.professionalSynthesis.conclusion
                            ].filter(Boolean).join(' ')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* REMOVED: EBIOS RM Attack Phases section - now auto-generated as phase summaries */}
          {/* Information is now consolidated in the Modes Opératoires section below */}

          {/* Unified Attack Paths & Phases Section - Collapsible */}
          {((currentScenario.modesOperatoires && currentScenario.modesOperatoires.length > 0) ||
            (currentScenario.operationalSteps || currentScenario.steps)) && (
              <div className="mb-4">
                <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
                  <button
                    onClick={() => setIsModesExpanded(!isModesExpanded)}
                    className="w-full bg-gradient-to-r from-purple-50 via-violet-50 to-purple-50 px-5 py-4 hover:from-purple-100 hover:via-violet-100 hover:to-purple-100 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-inset"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-violet-600 rounded-lg flex items-center justify-center shadow-sm">
                          <Database className="h-5 w-5 text-white" />
                        </div>
                        <div className="text-left">
                          <div className="flex items-center gap-3">
                            <h5 className="text-base font-semibold text-gray-900">{t('workshop4.modesOperatoires.title', 'Operational Modes & Attack Paths')}</h5>
                            {isGeneratingMode ? (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-purple-100 text-purple-500 border border-purple-200">
                                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                                {t('workshop4.modesOperatoires.generating', "Génération par l'Assistant ACG en cours...")}
                              </span>
                            ) : !showModeChoice ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setGenerationError(null);
                                  setShowModeChoice(true);
                                }}
                                className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-purple-100 text-purple-700 border border-purple-300 hover:bg-purple-200 transition-all"
                              >
                                <Plus className="h-3.5 w-3.5 mr-1" />
                                {t('workshop4.operationalScenarios.editModal.modes.addMode', 'Ajouter un mode')}
                              </button>
                            ) : (
                              <div className="inline-flex gap-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setGenerationError(null);
                                    setShowModeChoice(false);
                                    onAddMode && onAddMode(currentScenario);
                                  }}
                                  className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-indigo-100 text-indigo-700 border border-indigo-300 hover:bg-indigo-200 transition-all"
                                >
                                  <Edit className="h-3 w-3 mr-1" />
                                  {t('workshop4.modesOperatoires.manual', 'Manual')}
                                </button>
                                {companyAiEnabled && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAIGenerateMode();
                                    }}
                                    className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-purple-100 text-purple-700 border border-purple-300 hover:bg-purple-200 transition-all"
                                  >
                                    <BrainCircuit className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                            )}
                            {generationError && (
                              <div className="text-xs text-red-600 mt-1">{generationError}</div>
                            )}
                          </div>
                          <p className="text-xs text-gray-600 mt-0.5">{t('workshop4.modesOperatoires.subtitle', 'Technical details and elementary actions')}</p>
                        </div>
                      </div>
                      <ChevronRight
                        className={`h-5 w-5 text-gray-600 transition-transform duration-300 ${isModesExpanded ? 'rotate-90' : ''
                          }`}
                      />
                    </div>
                  </button>

                  <div className={`transition-all duration-300 ease-in-out ${isModesExpanded ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'
                    } overflow-hidden`}>
                    <div className="p-6 bg-gradient-to-br from-white to-gray-50">
                      <div className="flex flex-wrap gap-2 mb-4">
                        <button
                          onClick={() => setModeTab('manual')}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${modeTab === 'manual'
                            ? 'bg-indigo-600 text-white shadow-sm'
                            : 'bg-white text-slate-600 border border-slate-200 hover:border-indigo-300'
                            }`}
                        >
                          {t('workshop4.modesOperatoires.tabManual', "Modes saisis par l'utilisateur")}
                        </button>
                        <button
                          onClick={() => setModeTab('ai')}
                          className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${modeTab === 'ai'
                            ? 'bg-purple-600 text-white shadow-sm'
                            : 'bg-white text-slate-600 border border-slate-200 hover:border-purple-300'
                            }`}
                        >
                          <BrainCircuit className="h-3 w-3" />
                          {t('workshop4.modesOperatoires.tabAi', 'Modes générés par Assistant ACG')}
                        </button>
                        {aiModeCount > 0 && (
                          <button
                            onClick={() => setModeTab('compare')}
                            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${modeTab === 'compare'
                              ? 'bg-slate-700 text-white shadow-sm'
                              : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-400'
                              }`}
                          >
                            <BarChart3 className="h-3 w-3" />
                            {t('workshop4.modesOperatoires.tabComparison', 'Comparaison')}
                          </button>
                        )}
                      </div>
                      {modeTab === 'compare' ? (
                        <ModeComparisonPanel
                          manualModes={manualModes}
                          aiModes={aiModes}
                          operationalSteps={currentScenario.operationalSteps || currentScenario.steps || []}
                          elementaryActions={currentScenario.elementaryActions || []}
                          analysisId={analysisId}
                          attackPath={selectedAttackPath}
                          businessContext={currentScenario.businessContext || null}
                          aiEnabled={companyAiEnabled}
                          savedCritique={currentScenario.securityCritique}
                          onSaveCritique={(critique) =>
                            onSaveCritique && onSaveCritique({ ...currentScenario, securityCritique: critique })
                          }
                        />
                      ) : modeTab === 'manual' && manualModeCount === 0 ? (
                        <div className="p-6 text-center text-sm text-slate-500 border border-slate-200 rounded-lg bg-white">
                          {t('workshop4.modesOperatoires.noManualModes', 'Aucun mode élaboré par l\'utilisateur')}
                        </div>
                      ) : modeTab === 'ai' && aiModeCount === 0 ? (
                        <div className="p-6 text-center text-sm text-slate-500 border border-slate-200 rounded-lg bg-white">
                          {t('workshop4.modesOperatoires.noAiModes')}
                        </div>
                      ) : (
                        <ModesOperatoiresDisplay
                          modesOperatoires={visibleModes}
                          vizModes={allModes}
                          operationalSteps={currentScenario.operationalSteps || currentScenario.steps || []}
                          elementaryActions={currentScenario.elementaryActions || []}
                          scenario={currentScenario}
                          showPhases={true}
                          onPhaseVisualization={onPhaseVisualization}
                          selectedAttackPath={selectedAttackPath}
                          className=""
                          onDeleteMode={handleDeleteMode}
                          onToggleRetained={handleToggleRetained}
                          onCaptureReady={onCaptureReady}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}


          {/* Generation metadata */}
          {currentScenario.metadata && (
            <div className="border-t border-gray-200 pt-4 mt-4">
              <div className="flex items-center text-xs text-gray-500 space-x-4">
                {currentScenario.metadata.generationDate && (
                  <div className="flex items-center">
                    <Calendar className="h-3 w-3 mr-1" />
                    {t('workshop4.operationalScenarios.scenarioManager.generatedOn', 'Generated on')} {new Date(currentScenario.metadata.generationDate).toLocaleDateString()}
                  </div>
                )}
                {currentScenario.metadata.aiModel && (
                  <div>
                    {t('workshop4.operationalScenarios.scenarioManager.model', 'Model')}: {currentScenario.metadata.aiModel}
                  </div>
                )}
                {currentScenario.generationAttempt && (
                  <div>
                    {t('workshop4.operationalScenarios.scenarioManager.attempt', 'Attempt')} #{currentScenario.generationAttempt}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Vraisemblance Matrix Modal */}
      <VraisemblanceMatrixModal
        isOpen={showVraisemblanceMatrix}
        onClose={() => setShowVraisemblanceMatrix(false)}
      />

      {/* Delete Mode Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModeState.isOpen}
        onClose={() => setDeleteModeState({ isOpen: false, modeId: null, modeName: '' })}
        onConfirm={handleConfirmDeleteMode}
        itemName={deleteModeState.modeName}
        title="Supprimer le mode opératoire"
        message={`Êtes-vous sûr de vouloir supprimer le mode "${deleteModeState.modeName}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
      />

      {/* AI Generated Mode Preview */}
      {showAIPreview && aiGeneratedResult && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <BrainCircuit className="h-5 w-5 text-purple-600" />
                  {t('workshop4.modesOperatoires.aiPreview', "Mode généré par l'Assistant ACG")}
                </h3>
                <button
                  onClick={handleRefuseAIMode}
                  className="p-1 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <XCircle className="h-5 w-5 text-slate-400" />
                </button>
              </div>

              {/* Mode info */}
              <div className="mb-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                <div className="text-sm font-bold text-slate-800 mb-1">
                  {aiGeneratedResult.mode?.name || t('workshop4.modesOperatoires.unnamedMode', 'Mode sans nom')}
                </div>
                {aiGeneratedResult.mode?.description && (
                  <div className="text-xs text-slate-600">{aiGeneratedResult.mode.description}</div>
                )}
                {aiGeneratedResult.newActions?.length > 0 && (
                  <div className="mt-2 text-xs text-purple-600 font-medium">
                    {aiGeneratedResult.newActions.length} nouvelle(s) action(s) élémentaire(s) proposée(s)
                  </div>
                )}
              </div>

              {/* ReactFlow visualization */}
              <div className="mb-4 border border-slate-200 rounded-xl overflow-hidden" style={{ height: 400 }}>
                <ModesOperatoiresFlowVisualization
                  modesOperatoires={[
                    ...(currentScenario?.modesOperatoires || []),
                    {
                      id: 'mode_ai_preview',
                      name: aiGeneratedResult.mode?.name || 'Preview',
                      path: aiGeneratedResult.mode?.path || [],
                      vraisemblance: 2,
                    },
                  ]}
                  operationalSteps={(currentScenario?.elementaryActions || []).concat(
                    currentScenario?.operationalSteps || currentScenario?.steps || [],
                    (aiGeneratedResult.newActions || []).map((a) => ({
                      elementaryActionId: a.elementaryActionId,
                      phase: a.phase,
                      technique: a.name,
                      name: a.name,
                      description: a.description || '',
                      successProbability: a.ps,
                      technicalDifficulty: a.dt,
                      tools: a.tools || [],
                      duration: a.duration || '',
                    }))
                  )}
                  scenario={currentScenario}
                  isInline={true}
                  className=""
                  onCaptureReady={(fn) => { previewCaptureRef.current = fn; }}
                />
              </div>

              {/* Accept / Retry buttons */}
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={handleRetryAIMode}
                  disabled={isGeneratingMode}
                  className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 transition-all"
                >
                  {isGeneratingMode ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <BrainCircuit className="h-4 w-4 mr-2" />
                  )}
                  {t('workshop4.modesOperatoires.retry', 'Regénérer')}
                </button>
                <button
                  onClick={handleAcceptAIMode}
                  className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium bg-purple-600 text-white hover:bg-purple-700 transition-all shadow-sm"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {t('workshop4.modesOperatoires.accept', 'Accepter le mode')}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ScenarioManager;
